import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { selectValidation } from 'src/store/selectors/Validation';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import { clearValidation } from 'src/store/actions/suites';
import {
  useServiceRequestSchedulingAPI,
  UseServiceRequestSchedulingAPIReturn,
} from './useServiceRequestSchedulingAPI';
import {
  isWithinPreferredTimes,
  toAvailabilityMode,
} from './schedulingHelpers';
import {
  usePreferredServiceTimesAPI,
  UsePreferredServiceTimesAPIReturn,
} from './usePreferredServiceTimesAPI';
import {
  IServiceRequestDetail,
  IServiceRequestPreferredServiceTime,
} from 'src/services/api/ServiceRequest/apiContracts';
import {
  AvailabilityMode,
  IAvailableServiceTimeDay,
} from 'src/services/api/UserManagement/apiContracts';
import { flattenPreferredTimes } from 'src/components/ServiceRequestForm/TimeSlot/timeSlotHelpers';

interface UseServiceRequestSchedulingOrchestratorParams {
  serviceRequest: IServiceRequestDetail;
  onClose: () => void;
  onEventScheduled: (data: any) => Promise<void> | void;
}

export interface UseServiceRequestSchedulingOrchestratorReturn {
  isLoading: boolean;
  scheduledAt: any | null;
  scheduledAtLabel: string;
  availableServiceTimes: IAvailableServiceTimeDay[];
  isLoadingAvailability: boolean;
  isScheduling: boolean;
  hasPreferredTimes: boolean;

  preferredDays: string[];
  preferredEntries: string[];
  activePreferredDay: string | null;
  onPreferredDaySelect: (day: string) => void;

  showPreferenceMismatchModal: boolean;
  onConfirmScheduleAnyway: () => Promise<void>;
  onCancelScheduleAnyway: () => void;

  propertyName: string;
  propertyAddress: string;
  suiteNumber: string;
  homeownerName: string;
  homeownerProfileImage: string;

  onScheduledAtChange: (scheduledAt: any) => void;
  onScheduleClick: () => Promise<void>;
}

const SCHEDULED_SLOT_FORMAT: string = 'YYYY-MM-DD HH:mm A';

export const useServiceRequestSchedulingOrchestrator = ({
  serviceRequest,
  onClose,
  onEventScheduled,
}: UseServiceRequestSchedulingOrchestratorParams): UseServiceRequestSchedulingOrchestratorReturn => {
  const dispatch = useDispatch();

  // TODO: phase N — schedule saves still report completion through the global
  // validation/FormLoader buses; listen until that flow returns a result directly.
  const validation: any = useSelector(selectValidation);
  const formLoader: any = useSelector(selectFormLoader);

  const {
    availableServiceTimes,
    isLoadingAvailability,
    fetchAvailableServiceTimes,
  }: UseServiceRequestSchedulingAPIReturn = useServiceRequestSchedulingAPI();

  const { fetchPreferredServiceTimes }: UsePreferredServiceTimesAPIReturn =
    usePreferredServiceTimesAPI();

  const [preferredServiceTimes, setPreferredServiceTimes] = useState<
    IServiceRequestPreferredServiceTime[]
  >([]);
  const [activePreferredDay, setActivePreferredDay] = useState<string | null>(
    null
  );
  const [scheduledAt, setScheduledAt] = useState<any | null>(null);
  const [isScheduling, setIsScheduling] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [showPreferenceMismatchModal, setShowPreferenceMismatchModal] =
    useState<boolean>(false);

  const initializeScheduling = async () => {
    const preferredTimes: IServiceRequestPreferredServiceTime[] =
      await fetchPreferredServiceTimes(serviceRequest.id);

    setPreferredServiceTimes(preferredTimes);
    setActivePreferredDay(preferredTimes[0]?.day ?? null);
    setIsInitializing(false);

    if (preferredTimes.length === 0) {
      console.warn(
        'ServiceRequestScheduling: service request has no preferred service times; skipping availability fetch.'
      );

      return;
    }

    const availabilityModeBasedOnTenant: AvailabilityMode =
      toAvailabilityMode(preferredTimes);

    await fetchAvailabilityForProvider(availabilityModeBasedOnTenant);
  };

  useEffect(() => {
    void initializeScheduling();
  }, []);

  const closeWhenScheduleValidated = () => {
    if (validation.status !== true || validation.message !== 'Schedule') {
      return;
    }

    onClose();
    dispatch(clearValidation() as any);
  };

  useEffect(() => {
    closeWhenScheduleValidated();
  }, [validation, onClose, dispatch]);

  const fetchAvailabilityForProvider = async (mode: AvailabilityMode) => {
    const serviceProviderId: string | undefined =
      serviceRequest?.serviceProvider?.id;

    if (!serviceProviderId) {
      console.warn(
        'ServiceRequestScheduling: availability requested without a service provider on the service request'
      );

      return;
    }

    await fetchAvailableServiceTimes(serviceProviderId, mode);
  };

  const onScheduledAtChange = (value: any) => {
    setScheduledAt(value);
  };

  const onPreferredDaySelect = (day: string) => {
    setActivePreferredDay(day);
  };

  const submitSchedule = async () => {
    setIsScheduling(true);

    try {
      await onEventScheduled({ name: 'Scheduled', scheduledAt });
    } finally {
      setIsScheduling(false);
    }
  };

  const onScheduleClick = async () => {
    if (!scheduledAt) {
      console.warn(
        'ServiceRequestScheduling: onScheduleClick called without a scheduled time'
      );

      return;
    }

    const matchesHomeownerPreference: boolean = isWithinPreferredTimes(
      scheduledAt,
      preferredServiceTimes
    );

    if (!matchesHomeownerPreference) {
      setShowPreferenceMismatchModal(true);

      return;
    }

    await submitSchedule();
  };

  const onConfirmScheduleAnyway = async () => {
    await submitSchedule();

    setShowPreferenceMismatchModal(false);
  };

  const onCancelScheduleAnyway = () => {
    setShowPreferenceMismatchModal(false);
  };

  const hasPreferredTimes: boolean = preferredServiceTimes.length > 0;
  const preferredDays: string[] = preferredServiceTimes.map(
    (preferredDay: IServiceRequestPreferredServiceTime) => preferredDay.day
  );
  const preferredEntries: string[] =
    flattenPreferredTimes(preferredServiceTimes);

  const scheduledAtLabel: string = scheduledAt
    ? moment(scheduledAt, SCHEDULED_SLOT_FORMAT).format(
        'dddd, MMMM D [at] h:mm A'
      )
    : '';

  const property: any = serviceRequest?.suite?.property;
  const address: any = property?.propertyAddress;

  const propertyName: string = property?.name ?? '';
  const propertyAddress: string = `${address?.addressLine1 ?? ''}, ${address?.city ?? ''} ${address?.postalCode ?? ''}`;
  const suiteNumber: string = serviceRequest?.suite?.number ?? '';
  const homeownerName: string = serviceRequest?.suite?.homeOwner?.name ?? '';
  const homeownerProfileImage: string =
    serviceRequest?.suite?.homeOwner?.profileImage ?? '';

  const isLoading: boolean = isInitializing || Boolean(formLoader);

  return {
    isLoading,
    scheduledAt,
    scheduledAtLabel,
    availableServiceTimes,
    isLoadingAvailability,
    isScheduling,
    hasPreferredTimes,

    preferredDays,
    preferredEntries,
    activePreferredDay,
    onPreferredDaySelect,

    showPreferenceMismatchModal,
    onConfirmScheduleAnyway,
    onCancelScheduleAnyway,

    propertyName,
    propertyAddress,
    suiteNumber,
    homeownerName,
    homeownerProfileImage,

    onScheduledAtChange,
    onScheduleClick,
  };
};
